import PublicLayout from "@/components/PublicLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Phone, Mail, MapPin, Clock, CheckCircle2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "General enquiry", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      toast.success("Message received! We'll reply within 1 business day.");
      setForm({ name: "", email: "", phone: "", subject: "General enquiry", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PublicLayout>
      <section className="gradient-radial-blue">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="section-label">Contact Us</div>
          <h1 className="mt-2 font-display font-bold text-4xl sm:text-5xl text-[#0A192F]">We&apos;re here to help</h1>
          <p className="mt-3 text-slate-600 max-w-2xl">Reach us for pre-sales, order support, or partnership queries. Response within 1 business day.</p>
        </div>
      </section>

      <section className="py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-1 space-y-4">
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <div className="font-display font-semibold text-[#0A192F]">SHREE ADARSH INFOTECH</div>
              <div className="mt-3 space-y-3 text-sm text-slate-600">
                <div className="flex gap-3"><MapPin className="w-4 h-4 mt-0.5 text-[#1E3A8A]" /><span>Q1 &amp; Q2, Jain Building, Main Market, Sandiya, Sojat, Pali, Rajasthan – 306306, India</span></div>
                <div className="flex gap-3"><Phone className="w-4 h-4 mt-0.5 text-[#1E3A8A]" /><a href="tel:8448440219" className="hover:text-[#1E3A8A]">8448440219</a></div>
                <div className="flex gap-3"><Mail className="w-4 h-4 mt-0.5 text-[#1E3A8A]" /><a href="mailto:support@way2efiling.com" className="hover:text-[#1E3A8A] break-all">support@way2efiling.com</a></div>
                <div className="flex gap-3"><Clock className="w-4 h-4 mt-0.5 text-[#1E3A8A]" /><span>Mon – Sat, 10:00 AM – 7:00 PM IST</span></div>
                <div className="text-xs pt-2 text-slate-500">GSTIN: <span className="font-mono text-slate-700">08KKOPS0455P1Z2</span></div>
              </div>
              <div className="mt-4 flex gap-2">
                <a href="tel:8448440219" className="flex-1"><Button className="w-full bg-[#1E3A8A] hover:bg-[#1E40AF]" data-testid="call-now-btn"><Phone className="w-4 h-4 mr-1" /> Call Now</Button></a>
                <a href="mailto:support@way2efiling.com" className="flex-1"><Button variant="outline" className="w-full" data-testid="email-support-btn"><Mail className="w-4 h-4 mr-1" /> Email</Button></a>
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 overflow-hidden">
              <iframe
                title="Way2Efiling office"
                src="https://www.google.com/maps?q=Sojat,+Pali,+Rajasthan&output=embed"
                className="w-full h-56 border-0"
                loading="lazy"
              />
            </div>
          </div>

          <div className="lg:col-span-2 rounded-xl border border-slate-200 bg-white p-6">
            <div className="section-label">Send a message</div>
            <h2 className="mt-2 font-display font-semibold text-2xl text-[#0A192F]">Tell us how we can help</h2>

            {done ? (
              <div className="mt-6 rounded-lg border border-emerald-200 bg-emerald-50 p-6 text-center" data-testid="contact-done">
                <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <h3 className="mt-3 font-display font-bold text-xl text-emerald-900">Thanks — we got your message</h3>
                <p className="mt-2 text-sm text-emerald-800 max-w-md mx-auto">
                  A confirmation copy is on its way to your inbox and our team has been alerted at
                  <span className="font-semibold"> support@way2efiling.com</span>. We'll reply within 1 business day.
                </p>
                <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setDone(false)}
                    data-testid="contact-send-another-btn"
                  >
                    Send another message
                  </Button>
                  <a href="tel:8448440219">
                    <Button className="bg-[#1E3A8A] hover:bg-[#1E40AF]" data-testid="contact-done-call-btn">
                      <Phone className="w-4 h-4 mr-1" /> Call 8448440219
                    </Button>
                  </a>
                </div>
              </div>
            ) : (
            <form onSubmit={submit} className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="contact-form">
              <div>
                <Label>Name</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name-input" />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email-input" />
              </div>
              <div>
                <Label>Phone (optional)</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })} placeholder="9876543210" inputMode="numeric" data-testid="contact-phone-input" />
              </div>
              <div>
                <Label>Subject</Label>
                <Input required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="contact-subject-input" />
              </div>
              <div className="sm:col-span-2">
                <Label>Message</Label>
                <Textarea rows={5} required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message-input" />
              </div>
              <div className="sm:col-span-2">
                <Button type="submit" disabled={loading} className="bg-[#1E3A8A] hover:bg-[#1E40AF]" data-testid="contact-submit-btn">
                  {loading ? "Sending…" : "Send Message"}
                </Button>
              </div>
            </form>
            )}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
